import { getBlockedUsers } from '../api/block';

/**
 * 내가 차단한 사용자 ID 집합을 클라이언트에 보관한다.
 *
 * - authSession 과 동일하게 sessionStorage 에 캐싱해 새로고침/탭 이동 시 유지한다.
 * - 로그인 직후 {@link refreshBlockedUserIds} 로 서버 목록을 적재한다.
 * - 차단/해제 시 낙관적으로 set 을 갱신하고 구독자에게 통지한다.
 * - userId 는 64bit Snowflake — 항상 string 으로 다룬다 (number 변환 금지).
 */

const BLOCKED_USERS_KEY = 'bander.blockedUserIds';

type Listener = () => void;

let blockedIds = new Set<string>();
let hydratedFromStorage = false;
const listeners = new Set<Listener>();

function readFromStorage(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set<string>();
  }

  const raw = window.sessionStorage.getItem(BLOCKED_USERS_KEY);
  if (!raw) {
    return new Set<string>();
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    window.sessionStorage.removeItem(BLOCKED_USERS_KEY);
  }

  return new Set<string>();
}

function writeToStorage(ids: Set<string>) {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(BLOCKED_USERS_KEY, JSON.stringify(Array.from(ids)));
}

function ensureHydrated() {
  if (hydratedFromStorage) {
    return;
  }
  blockedIds = readFromStorage();
  hydratedFromStorage = true;
}

function notify() {
  listeners.forEach((listener) => listener());
}

function commit(next: Set<string>) {
  blockedIds = next;
  hydratedFromStorage = true;
  writeToStorage(next);
  notify();
}

/** 현재 차단 ID 집합 (읽기 전용 복사본). */
export function getBlockedUserIds(): ReadonlySet<string> {
  ensureHydrated();
  return blockedIds;
}

/** 특정 userId 가 차단되어 있는지. null/undefined 는 false. */
export function isUserBlocked(userId: string | number | null | undefined): boolean {
  if (userId === null || userId === undefined) {
    return false;
  }
  ensureHydrated();
  return blockedIds.has(String(userId));
}

/** 차단 ID 를 낙관적으로 추가. */
export function addBlockedUserId(userId: string | number) {
  ensureHydrated();
  const id = String(userId);
  if (blockedIds.has(id)) {
    return;
  }
  const next = new Set(blockedIds);
  next.add(id);
  commit(next);
}

/** 차단 ID 를 낙관적으로 제거. */
export function removeBlockedUserId(userId: string | number) {
  ensureHydrated();
  const id = String(userId);
  if (!blockedIds.has(id)) {
    return;
  }
  const next = new Set(blockedIds);
  next.delete(id);
  commit(next);
}

/** 서버 목록 전체로 set 을 교체 (로그인 직후 적재). */
export function setBlockedUserIds(ids: ReadonlyArray<string | number>) {
  commit(new Set(ids.map((id) => String(id))));
}

/** 로그아웃/세션 만료 시 캐시 정리. */
export function clearBlockedUserIds() {
  blockedIds = new Set<string>();
  hydratedFromStorage = true;
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(BLOCKED_USERS_KEY);
  }
  notify();
}

/**
 * 서버에서 차단 목록을 페이지네이션하며 모두 읽어 set 을 적재한다.
 * 로그인 직후 1회 호출. 실패해도 throw 하지 않고 조용히 무시한다
 * (차단 필터는 부가 기능이며 핵심 플로우를 막지 않는다).
 */
export async function refreshBlockedUserIds(): Promise<void> {
  try {
    const collected: string[] = [];
    let page = 0;
    // 무한 루프 방지용 안전 상한 (1000명 * 100/page).
    for (let guard = 0; guard < 1000; guard += 1) {
      const result = await getBlockedUsers({ page, size: 100 });
      for (const item of result.items) {
        collected.push(String(item.userId));
      }
      if (!result.hasNext) {
        break;
      }
      page += 1;
    }
    setBlockedUserIds(collected);
  } catch {
    // 무시: 차단 목록 적재 실패는 비차단 동작이다.
  }
}

/** 차단 set 변경 구독. unsubscribe 함수를 반환한다. */
export function subscribeBlockedUserIds(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
